import { useEffect, useState, useCallback, useMemo } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import classNames from 'classnames'
import qs from 'qs'
import snakeCase from 'lodash/snakeCase'
import { FormattedMessage, useIntl } from 'react-intl'

import { constructFilters } from '../../../helpers/listings/filters'
import { camelizeKeys } from '../../../helpers/utils'

// Actions
import {
  searchListingsForListView,
  fetchSearchListing,
  toggleListingSearchModal,
  setPreviouslyViewedListingId,
  setSelectedListingId,
} from '../../../actions/listings'
import { useListings } from './hooks/useListings'
import { loadSuggestedLocations, setFilter } from '../../../actions/filters'

// Components
import { AlertToggle } from './AlertToggle'
import { Filters } from '../Filters'
import { HomeSearchFooter } from '../HomeSearchFooter'
import ListingsMap from './ListingsMap'
import ListingsSort from './ListingsSort'
import {
  BotHeadSpinWithMessage,
  Button,
  OverlayPanel,
  Text,
} from '../..'
import OverlayMask from '../../shared/Routing/OverlayMask'
import PreferencesPanel from '../../shared/PreferencesPanel'

// Constants
import { OVERLAY_HELPERS } from '../../../constants/navigation'
import {
  LISTINGS_SEARCH,
  LOCATION_LEVELS,
  LOCATION,
  POSTAL_CODE,
  STREET,
  NEIGHBOURHOOD,
  COUNTY,
  CITY,
  STATE,
  MAX_PRICE_CENTS,
  MAX_SQFT,
  MAX_YEAR_BUILT,
  MIN_BATHROOMS_COUNT,
  MIN_BEDROOMS_COUNT,
  MIN_PRICE_CENTS,
  MIN_SQFT,
  MIN_YEAR_BUILT,
  PROPERTY_TYPES,
  ORDER,
  PAGE,
  STATUSES,
  LISTINGS_DELAY_UNTIL_SHOW_LOADER,
  LISTINGS_MIN_LOADER_DISPLAY_TIME,
} from '../../../constants/listings'

import useViewport from '../../../hooks/useViewport'
import { useDelayLoading } from '../../../hooks'
import { formatAbbrNumber } from '../../../helpers/formatters'

// Selectors
import { selectCustomerId } from '../../../store/selectors/customerProfile'
import { selectFilter, selectFiltersCount } from '../../../store/selectors/filters'
import {
  selectHomebotListingsSearchLoading,
  selectSearchListings,
  selectSearchListingsTotal,
  selectPreviouslyViewedListingId,
} from '../../../store/selectors/listings'

// Styles
import { SearchIcon } from '../Search/SearchBar/SearchIcon'
import { ListView } from './ListView'
import styles from './Listings.module.scss'
import { ListingsEmptyState } from './ListingsEmptyState/ListingsEmptyState'
import { ListViewFilterViewSpacingWrapper } from '../ListViewFilterViewSpacingWrapper'
import { FETCH_HOMEBOT_LISTINGS_FAILURE } from '../../../actions/actionTypes'

const Listings = () => {
  const dispatch = useDispatch()

  const intl = useIntl()

  const previouslyViewedListingId = useSelector(selectPreviouslyViewedListingId)
  const listings = useSelector(selectSearchListings)
  const listingsTotal = useSelector(selectSearchListingsTotal)
  const filter = useSelector(selectFilter)
  const filtersCount = useSelector(selectFiltersCount)
  const customerId = useSelector(selectCustomerId)

  const [markerOnMapClicked, setMarkerOnMapClicked] = useState(false)
  const [showFilters, setShowFilters] = useState(false)
  const [isMobileViewExpanded, setIsMobileViewExpanded] = useState(false)

  const { isMediumOrAbove } = useViewport()

  const {
    statefulListings,
    listingsDispatch,
    loadListingState,
    assignListingRef,
  } = useListings()

  const queryParams = camelizeKeys(
    qs.parse(window.location.search, {
      ignoreQueryPrefix: true,
    })
  )

  const scrollBehavior = useMemo(() => {
    return isMediumOrAbove ? 'smooth' : 'auto'
  }, [isMediumOrAbove])

  const isLoadingListings = useSelector(selectHomebotListingsSearchLoading)

  const isDelayLoading = useDelayLoading(
    isLoadingListings,
    LISTINGS_DELAY_UNTIL_SHOW_LOADER,
    LISTINGS_MIN_LOADER_DISPLAY_TIME
  )

  // splitting apart this array for useEffect dependencies array
  const [propertyType0, propertyType1, propertyType2, propertyType3] =
    queryParams?.[PROPERTY_TYPES] || [
      undefined,
      undefined,
      undefined,
      undefined,
    ]

  // splitting apart this array for useEffect dependencies array
  const [listingStatus0, listingStatus1] = queryParams?.[STATUSES] || [
    undefined,
    undefined,
  ]

  // restore scroll afer user returns from visiting listing details
  useEffect(() => {
    // hack to allow refs on "statefulListings" to be set. Would love a better way to trigger effect when that happens
    const timer1 = setTimeout(() => {
      const previouslyClickedListing = statefulListings.find(
        l => l.id === previouslyViewedListingId
      )
      if (previouslyClickedListing) {
        previouslyClickedListing?.ref?.current?.scrollIntoView({
          behavior: scrollBehavior,
          block: 'center',
        })
      } else {
        dispatch(
          fetchSearchListing(
            customerId,
            previouslyViewedListingId,
            LISTINGS_SEARCH
          )
        ).then(() => {
          const element = document.getElementById('list-bottom')

          element?.scrollIntoView({
            behavior: scrollBehavior,
            block: 'center',
          })
        })
      }
    }, 1000)

    // we want the focus ring to stay in place for a few seconds so the user can get oriented before allowing hovers to change which listing is highlighted -> see handleHoverAndFocusChange()
    const timer2 = setTimeout(() => {
      dispatch(setPreviouslyViewedListingId(''))
    }, 5000)

    return () => {
      clearTimeout(timer1)
      clearTimeout(timer2)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previouslyViewedListingId, statefulListings])

  useEffect(() => {
    listingsDispatch({ type: loadListingState, listings: listings })
  }, [listings, listingsDispatch, loadListingState])

  useEffect(() => {
    LOCATION_LEVELS.find(currentLocationLevel => {
      const value = queryParams[currentLocationLevel]
      if (value) {
        dispatch(
          loadSuggestedLocations({
            query: `${value} ${snakeCase(currentLocationLevel)}`,
          })
        )
      }
      return value
    })

    const searchFilters = constructFilters(queryParams)
    dispatch(setFilter(searchFilters))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dispatch,
    queryParams?.[POSTAL_CODE],
    queryParams?.[STREET],
    queryParams?.[NEIGHBOURHOOD],
    queryParams?.[COUNTY],
    queryParams?.[CITY],
    queryParams?.[STATE],
    queryParams?.[MIN_PRICE_CENTS],
    queryParams?.[MAX_PRICE_CENTS],
    queryParams?.[MIN_BATHROOMS_COUNT],
    queryParams?.[MIN_BEDROOMS_COUNT],
    queryParams?.[MIN_SQFT],
    queryParams?.[MAX_SQFT],
    queryParams?.[MIN_YEAR_BUILT],
    queryParams?.[MAX_YEAR_BUILT],
    propertyType0,
    propertyType1,
    propertyType2,
    propertyType3,
    listingStatus0,
    listingStatus1,
  ])

  useEffect(() => {
    const { location } = filter

    if (location?.id) {
      dispatch(
        searchListingsForListView(customerId, {
          ...filter,
        })
      )
    } else {
      dispatch({
        type: FETCH_HOMEBOT_LISTINGS_FAILURE,
        tag: LISTINGS_SEARCH,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dispatch,
    customerId,
    filter,
    filter?.[LOCATION],
    filter?.[ORDER],
    filter?.[PAGE],
    filter?.[MIN_PRICE_CENTS],
    filter?.[MAX_PRICE_CENTS],
    filter?.[MIN_BATHROOMS_COUNT],
    filter?.[MIN_BEDROOMS_COUNT],
    filter?.[MIN_SQFT],
    filter?.[MAX_SQFT],
    filter?.[MIN_YEAR_BUILT],
    filter?.[MAX_YEAR_BUILT],
    filter?.[PROPERTY_TYPES],
    filter?.[STATUSES],
  ])

  const handleHoverAndFocusChange = useCallback(
    listingId => {
      // if user returns from details page, we want to add focus ring to that listing, and we don't want hovering another listing to remove that focus ring
      // after a short delay, previouslyViewedListingId will be reset, and we will bypass the early return here
      if (previouslyViewedListingId) {
        return
      }
      setMarkerOnMapClicked(false)
      dispatch(setSelectedListingId(listingId))
    },
    [dispatch, previouslyViewedListingId]
  )

  const onMarkerClick = useCallback(
    marker => {
      if (marker?.id) {
        dispatch(setSelectedListingId(marker?.id))
        setMarkerOnMapClicked(true)
      }

      const clickedListing = statefulListings.find(
        listing => listing.id === marker.id
      )

      if (clickedListing) {
        clickedListing?.ref?.current?.scrollIntoView({
          behavior: scrollBehavior,
          block: 'start',
        })
      }

      if (!clickedListing) {
        dispatch(
          fetchSearchListing(customerId, marker.id, LISTINGS_SEARCH)
        ).then(() => {
          const element = document.getElementById('list-bottom')

          element?.scrollIntoView({
            behavior: scrollBehavior,
            block: 'center',
          })
        })
      }

      setIsMobileViewExpanded(true)
    },
    [customerId, dispatch, statefulListings, scrollBehavior]
  )

  const getListingsListView = () => {
    if (isDelayLoading) {
      return (
        <BotHeadSpinWithMessage className={styles.loadingContainer}>
          <FormattedMessage
            id='BotHeadSpinWithMessage.ListingsListView'
            defaultMessage='Loading homes for you...'
          />
        </BotHeadSpinWithMessage>
      )
    }
    if (
      !isLoadingListings &&
      (!statefulListings || statefulListings.length === 0)
    ) {
      return <ListingsEmptyState />
    }
    return (
      <ListView
        listings={statefulListings}
        listingsDispatch={listingsDispatch}
        customerId={customerId}
        handleHoverAndFocusChange={handleHoverAndFocusChange}
        setMarkerOnMapClicked={setMarkerOnMapClicked}
        assignListingRef={assignListingRef}
      />
    )
  }

  const getListingsTotalDisplay = useCallback(() => {
    if (!listingsTotal || listingsTotal === 0) {
      return null
    }
    if (listingsTotal === 1) {
      return '1 home'
    }
    return `${formatAbbrNumber(listingsTotal)} homes`
  }, [listingsTotal])

  return (
    <>
      <OverlayPanel
        back={OVERLAY_HELPERS.preferences.getOrigin()}
        path={OVERLAY_HELPERS.preferences.buildPath()}
        title={OVERLAY_HELPERS.getTitle('preferences', intl.formatMessage)}
        disableAnchor
        component={PreferencesPanel}
      />
      <OverlayMask className={styles.hideOverlayScrollbar} isExact>
        <div className={styles.listingsWrapper}>
          <ListViewFilterViewSpacingWrapper
            isMobileViewExpanded={isMobileViewExpanded}
            setIsMobileViewExpanded={setIsMobileViewExpanded}
          >
            {showFilters ? (
              <Filters onClose={() => setShowFilters(false)} />
            ) : (
              <div className={styles.listingsResults}>
                <div className={styles.searchWrapper}>
                  <Button
                    onClick={() => dispatch(toggleListingSearchModal(true))}
                    className={styles.searchBarBtn}
                    compact
                  >
                    <div className={styles.placeholder}>
                      <SearchIcon className={styles.svg} />
                      <Text
                        className={styles.cta}
                        variant='neutralCool'
                        variantCount='900'
                        element='span'
                      >
                        {filter?.displayName}
                      </Text>
                    </div>
                  </Button>
                  <AlertToggle />
                </div>
                <div className={styles.countWrapper}>
                  <Text
                    element='h2'
                    weight='bold'
                    align='left'
                    variant='neutralCool'
                    variantCount='900'
                  >
                    {getListingsTotalDisplay()}
                  </Text>
                  <Button
                    className={styles.filterButton}
                    data-qa='listingsResultsFilterButton'
                    aria-label='filter listings'
                    compact
                    onClick={() => setShowFilters(true)}
                  >
                    <FormattedMessage
                      id='HomeSearch.listings.filters.button'
                      defaultMessage='Filters'
                    />
                    <div className={styles.filtersCount}>{filtersCount}</div>
                  </Button>
                  <ListingsSort />
                </div>
                <div
                  className={classNames({
                    [styles.loadingWrapper]: isLoadingListings,
                  })}
                >
                  {getListingsListView()}
                </div>
                <div>
                  <div id='list-bottom' />
                  <HomeSearchFooter />
                </div>
              </div>
            )}
          </ListViewFilterViewSpacingWrapper>
          <div className={styles.map}>
            {filter.location?.id && (
              <ListingsMap
                filter={filter}
                listings={statefulListings}
                onMarkerClick={onMarkerClick}
                markerOnMapClicked={markerOnMapClicked}
                isDelayLoading={isDelayLoading}
                setIsMobileViewExpanded={setIsMobileViewExpanded}
                scrollBehavior={scrollBehavior}
              />
            )}
          </div>
        </div>
      </OverlayMask>
    </>
  )
}

export default Listings
